/**
 * Shared Parser Utilities
 *
 * Reusable helper functions for all component parsers.
 */

import { v4 as uuidv4 } from 'uuid'

import { DefaultVariableResult, VariableSelectFeatureFlag } from '@makyoapp/makyo-common'

import type {
  AiAspectRatio,
  AppBreakpoint,
  BaseTableDataSource,
  ColorAdaptiveValue,
  ColorType,
  ColorVariant,
  ComponentAlignment,
  CornerRadiusValue,
  GradientDirection,
  HorizontalAlignment,
  LimitInput,
  LimitSettings,
  ResponsiveValue,
  SpacingInputValue,
  SpacingUnit,
  TableDataSourceColumn,
  TableDataSourceColumnWithIcon,
  TextAlignment,
  TypographyKey,
  TypographyValue,
  VariableTextValue,
  VerticalAlignment,
} from './types'

export const BREAKPOINTS: AppBreakpoint[] = ['phone', 'phone-landscape', 'tablet', 'tablet-landscape', 'desktop', 'tv']
export const BREAKPOINT_PREFIXES: Record<string, AppBreakpoint> = {
  ph: 'phone',
  pl: 'phone-landscape',
  ta: 'tablet',
  tl: 'tablet-landscape',
  de: 'desktop',
  tv: 'tv',
}

/**
 * Deep clone and merge objects
 */
export function deepMerge<T>(target: T, source: Partial<T>): T {
  const output = JSON.parse(JSON.stringify(target)) as T

  if (typeof source !== 'object' || source === null) {
    return output
  }

  Object.keys(source).forEach((key) => {
    const sourceValue = source[key as keyof T]
    if (sourceValue !== undefined) {
      ;(output as any)[key] = sourceValue
    }
  })

  return output
}

/**
 * Create responsive value by applying same value to all breakpoints
 */
export function createResponsiveValue<T>(value: T): Record<AppBreakpoint, T> {
  return {
    phone: value,
    'phone-landscape': value,
    tablet: value,
    'tablet-landscape': value,
    desktop: value,
    tv: value,
  }
}

/**
 * Create spacing value from a number (in pixels)
 */
export function createSpacingValue(value: number) {
  return {
    top: { value, unit: 'px' as const },
    right: { value, unit: 'px' as const },
    bottom: { value, unit: 'px' as const },
    left: { value, unit: 'px' as const },
  }
}

/**
 * Parse select format string to DefaultVariableResult
 * Format: "text:<value>" | "icon:<value>" | "profile:<col-id>" | "sc:<table-id>:<col-id>:<row-number>" | etc.
 * See docs/app-builder/format/data/select.md for all formats
 */
export function parseSelectFormat(selectString: string): DefaultVariableResult {
  const colonIndex = selectString.indexOf(':')

  // Handle formats without colon (pc, pt, cdt, sid, uid)
  if (colonIndex === -1) {
    switch (selectString) {
      case 'pc':
        return {
          id: '',
          flag: VariableSelectFeatureFlag.PageCover,
        } as DefaultVariableResult
      case 'pt':
        return {
          id: '',
          flag: VariableSelectFeatureFlag.PageTitle,
        } as DefaultVariableResult
      case 'cdt':
        return {
          id: '',
          flag: VariableSelectFeatureFlag.CurrentDateTime,
        } as DefaultVariableResult
      case 'sid':
        return {
          id: '',
          flag: VariableSelectFeatureFlag.SessionID,
        } as DefaultVariableResult
      case 'uid':
        return {
          id: '',
          flag: VariableSelectFeatureFlag.UniqueID,
        } as DefaultVariableResult
      default:
        // Fallback to icon if no match
        return {
          id: '',
          flag: VariableSelectFeatureFlag.Icon,
          value: selectString,
        } as DefaultVariableResult
    }
  }

  const prefix = selectString.substring(0, colonIndex)
  const value = selectString.substring(colonIndex + 1)

  switch (prefix) {
    case 'text':
      return {
        id: '',
        flag: VariableSelectFeatureFlag.CustomText,
        value: value,
      } as DefaultVariableResult
    case 'icon':
      return {
        id: '',
        flag: VariableSelectFeatureFlag.Icon,
        value: value,
      } as DefaultVariableResult
    case 'cd':
      // ColumnData: cd:<col-id>
      // Note: type property is NOT added here - it's only for variable text nodes
      return {
        id: uuidv4(),
        flag: VariableSelectFeatureFlag.ColumnData,
        col: value,
        userSpecific: false,
      } as DefaultVariableResult
    case 'profile':
      // Note: type property is NOT added here - it's only for variable text nodes
      return {
        id: uuidv4(),
        flag: VariableSelectFeatureFlag.ProfileData,
        col: value,
        userSpecific: false,
      } as DefaultVariableResult
    case 'sc': {
      // Single Cell: sc:<table-id>:<col-id>:<row-number>
      const parts = value.split(':')
      if (parts.length >= 3) {
        return {
          id: '',
          flag: VariableSelectFeatureFlag.SingleCell,
          tbl: parts[0],
          col: parts[1],
          row: parts[2],
          userSpecific: false,
        } as DefaultVariableResult
      }
      // Invalid format, fallback
      return {
        id: '',
        flag: VariableSelectFeatureFlag.CustomText,
        value: value,
      } as DefaultVariableResult
    }
    case 'cr':
      // Current Row: cr:<col-id>
      return {
        id: '',
        flag: VariableSelectFeatureFlag.CurrentRow,
        col: value,
        userSpecific: false,
      } as DefaultVariableResult
    case 'image': {
      // Handle local image (no flag, isLocal: true): "image:local:/path/to/image.png"
      if (value.startsWith('local:')) {
        const imageValue = value.substring(6) // Everything after "local:"
        return {
          id: '',
          // No flag - local images don't have a flag (special case for images)
          isLocal: true,
          value: imageValue,
        } as unknown as DefaultVariableResult
      }
      // Handle internal image with col reference: "image:col:4z42Ta" or "image:col:4z42Ta:/path/to/image.png"
      if (value.startsWith('col:')) {
        const afterCol = value.substring(4) // Everything after "col:"
        // Check if there's a value after the col ID (format: "col:<col-id>:<value>")
        const colonIndex = afterCol.indexOf(':')
        let colId: string
        let imageValue: string = ''

        if (colonIndex !== -1) {
          // Has value: "4z42Ta:/path/to/image.png"
          colId = afterCol.substring(0, colonIndex)
          imageValue = afterCol.substring(colonIndex + 1)
        } else {
          // No value: "4z42Ta"
          colId = afterCol
        }

        return {
          id: '',
          flag: VariableSelectFeatureFlag.Image,
          col: colId,
          isLocal: true,
          value: imageValue, // Include the path/value if present
        } as DefaultVariableResult
      }
      return {
        id: '',
        flag: VariableSelectFeatureFlag.Image,
        value: value,
      } as DefaultVariableResult
    }
    case 'file':
      return {
        id: '',
        flag: VariableSelectFeatureFlag.File,
        value: value,
      } as DefaultVariableResult
    case 'rc':
      // Rich Text: rc:<markdown-content>
      return {
        id: '',
        flag: VariableSelectFeatureFlag.RichText,
        value: value,
      } as DefaultVariableResult
    case 'dt': {
      // Date Time: dt:<date-option>
      return {
        id: '',
        flag: VariableSelectFeatureFlag.DateTime,
        variant: value as any,
      } as DefaultVariableResult
    }
    case 'gv':
      // Global Variable: gv:<variable-name>
      return {
        id: '',
        flag: VariableSelectFeatureFlag.GlobalVariable,
        value: value,
      } as DefaultVariableResult
    default:
      // Unknown prefix, treat as icon
      return {
        id: '',
        flag: VariableSelectFeatureFlag.Icon,
        value: selectString,
      } as DefaultVariableResult
  }
}

/**
 * Parse text array format to VariableTextValue
 * Format: ["text:Hello", "sc:table:col:row", "cr:title"] or ["text:Hello World"]
 * See docs/app-builder/format/data/text.md for format details
 */
export function parseTextFormat(textArray: string[]): VariableTextValue {
  if (!Array.isArray(textArray) || textArray.length === 0) {
    return [{ type: 'paragraph' as const, children: [{ text: '' }] }]
  }

  // Parse each select string in the array
  const children = textArray.map((selectString) => {
    const parsed = parseSelectFormat(selectString.trim())
    // If it's a text select, extract just the text value
    if (parsed.flag === VariableSelectFeatureFlag.CustomText) {
      return { text: parsed.value as string }
    }
    // Otherwise, return as DefaultVariableResult with type property
    // The type property matches the flag value for VariableTextFeatureFlag
    // VariableTextFeatureFlag uses the same values as VariableSelectFeatureFlag
    return {
      ...parsed,
      type: parsed.flag,
      // Variable text nodes also have a children array with empty text
      children: [{ text: '' }],
    }
  })

  return [
    {
      type: 'paragraph' as const,
      children: children,
    },
  ]
}

/**
 * Extract plain text from VariableTextValue or raw label array for _Title field
 * Supports both parsed VariableTextValue format and raw string arrays like ["text:Full Name"]
 */
export function extractTextFromVariableText(variableText: VariableTextValue | string[] | string | undefined): string {
  if (!variableText) return ''
  if (typeof variableText === 'string') return variableText

  // If it's a raw array like ["text:Full Name"], parse it first
  if (Array.isArray(variableText) && variableText.length > 0) {
    // Check if it's already parsed VariableTextValue (has type and children)
    const firstItem = variableText[0]
    if (typeof firstItem === 'object' && firstItem !== null && 'type' in firstItem && 'children' in firstItem) {
      // Already parsed VariableTextValue format
      let text = ''
      for (const node of variableText) {
        if (typeof node === 'object' && node !== null && 'children' in node && Array.isArray(node.children)) {
          for (const child of node.children) {
            if (typeof child === 'object' && child !== null && 'text' in child && typeof child.text === 'string') {
              text += child.text
            }
          }
        }
      }
      return text.trim()
    } else {
      // Raw array format like ["text:Full Name"] - parse it first
      const parsed = parseToVariableText(variableText as string[])
      let text = ''
      for (const node of parsed) {
        if (typeof node === 'object' && node !== null && 'children' in node && Array.isArray(node.children)) {
          for (const child of node.children) {
            if (typeof child === 'object' && child !== null && 'text' in child && typeof child.text === 'string') {
              text += child.text
            }
          }
        }
      }
      return text.trim()
    }
  }

  return ''
}

/**
 * Convert plain text to VariableTextValue (backward compatibility)
 * This is a convenience function that wraps plain text in text: format
 */
export function parseToVariableText(text: string | string[]): VariableTextValue {
  // If it's already an array, parse as text format
  if (Array.isArray(text)) {
    return parseTextFormat(text)
  }

  // If it's a plain string, wrap it in text: format
  return parseTextFormat([`text:${text}`])
}

/**
 * Convert select format string to VariableSelectValue
 * Format: "icon:home" | "text:Hello" | "profile:username" | etc.
 */
export function parseToVariableSelect(selectString: string): DefaultVariableResult {
  return parseSelectFormat(selectString)
}

/**
 * Parse responsive format string with breakpoints
 * Format: "<default> <breakpoint>:<value> <breakpoint>:<value>"
 * Example: "body-medium ta:body-large de:body-extra-large"
 */
export function parseResponsiveFormat<T>(format: string, parseValue: (value: string) => T): ResponsiveValue<T> {
  const parts = format.trim().split(/\s+/)
  const result: Partial<Record<AppBreakpoint, T>> = {}
  const defaultParts: string[] = []

  for (const part of parts) {
    const colonIndex = part.indexOf(':')
    if (colonIndex === -1) {
      // No colon at all, this is a default value part
      defaultParts.push(part)
    } else {
      const prefix = part.substring(0, colonIndex)
      const value = part.substring(colonIndex + 1)
      const breakpoint = BREAKPOINT_PREFIXES[prefix]

      if (breakpoint) {
        // This is a breakpoint prefix (ta, de, pl, etc.)
        // Check if the value contains another colon (e.g., "ta:m:24px" or "ta:pt:12px")
        // If so, it's a breakpoint-prefixed spacing/alignment/etc. value
        const secondColonIndex = value.indexOf(':')
        if (secondColonIndex !== -1) {
          // Has nested format (e.g., "ta:m:24px", "ta:pt:12px", "ta:t:center")
          // This is a breakpoint-specific value with nested format
          result[breakpoint] = parseValue(value)
        } else {
          // Simple breakpoint value (e.g., "ta:body-large")
          // Check if it's a spacing format - if so, it might be ambiguous with spacing directions
          // But since we have a breakpoint prefix, we should treat it as breakpoint-specific
          const isSpacingFormat = /^\d+(?:\.\d+)?(px|rem|em|%)$/.test(value)
          if (!isSpacingFormat) {
            // Not a spacing format, safe to treat as breakpoint-specific
            result[breakpoint] = parseValue(value)
          } else {
            // It's a spacing format value after a breakpoint prefix
            // This is ambiguous - could be "pl:16px" (phone-landscape:16px) or padding-left:16px
            // For now, treat breakpoint prefix as higher priority
            result[breakpoint] = parseValue(value)
          }
        }
      } else {
        // Not a breakpoint prefix, check if it's a spacing direction
        // Spacing format: direction:number+unit (e.g., "pt:12px", "pl:16px", "m:16px")
        // Check if prefix is a spacing direction
        const spacingDirections = ['pt', 'pr', 'pb', 'pl', 'mt', 'mr', 'mb', 'ml', 'p', 'm', 'gap']
        const isSpacingDirection = spacingDirections.includes(prefix)

        if (isSpacingDirection) {
          // This is a spacing direction, treat as default value part
          defaultParts.push(part)
        } else {
          // Not a breakpoint prefix and not a spacing direction, treat as default value part
          defaultParts.push(part)
        }
      }
    }
  }

  // Apply default value to all breakpoints that weren't explicitly set
  // If no default parts, use the entire format string as fallback
  const defaultString = defaultParts.length > 0 ? defaultParts.join(' ') : format.trim()
  const finalValue = parseValue(defaultString)

  // Apply inheritance rules:
  // - phone-landscape derives from phone
  // - tablet-landscape derives from tablet
  // - desktop derives from tablet-landscape (if not explicitly set)
  // - tv derives from desktop
  const phone = result.phone ?? finalValue
  const tablet = result.tablet ?? finalValue
  const tabletLandscape = result['tablet-landscape'] ?? tablet
  const desktop = result.desktop ?? tabletLandscape

  return {
    phone: phone,
    'phone-landscape': result['phone-landscape'] ?? phone,
    tablet: tablet,
    'tablet-landscape': tabletLandscape,
    desktop: desktop,
    tv: result.tv ?? desktop,
  }
}

/**
 * Parse typography format string
 * Format: "<variant>" or "custom:<family>:<style>:<size>:<line-height>:<letter-spacing>:<transform>"
 * Example: "body-medium" or "custom:Roboto:600:18px:1.5:0.5px:none"
 */
export function parseTypographyFormat(format: string): ResponsiveValue<TypographyValue> {
  return parseResponsiveFormat(format, (value) => {
    // For now, we only support predefined variants
    // Custom typography parsing would require more complex logic
    return {
      typography: value as TypographyKey,
    }
  })
}

/**
 * Parse color format string
 * Format: "adaptive" | "solid:<color>" | "gradient:<color1>|<color2>:<direction>"
 * Can have light/dark mode: "<light-mode> <dark-mode>"
 * Example: "solid:primary-40" or "gradient:primary-40|primary-80:to-right"
 * Example with modes: "solid:primary-40 solid:primary-80"
 */
export function parseColorFormat(format: string): {
  colorType: ColorAdaptiveValue<ColorType>
  colorSolid: ColorAdaptiveValue<ColorVariant>
  colorStartGradient: ColorAdaptiveValue<ColorVariant>
  colorEndGradient: ColorAdaptiveValue<ColorVariant>
  colorGradientDirection: ColorAdaptiveValue<GradientDirection>
} {
  const parts = format.trim().split(/\s+/)
  const lightPart = parts[0] || 'adaptive'
  const darkPart = parts[1] || lightPart

  const parseColorPart = (part: string) => {
    if (part === 'adaptive') {
      return {
        type: 'adaptive' as ColorType,
        solid: 'primary-40' as ColorVariant,
        startGradient: 'primary-40' as ColorVariant,
        endGradient: 'primary-80' as ColorVariant,
        direction: 'to-right' as GradientDirection,
      }
    }

    // Handle adaptive: prefix with gradient format: "adaptive:primary-10|primary-5:to-right"
    if (part.startsWith('adaptive:')) {
      const gradientPart = part.substring(9) // Remove "adaptive:"
      const [colors, direction] = gradientPart.split(':')
      const [startColor, endColor] = colors.split('|')
      return {
        type: 'gradient' as ColorType,
        solid: 'primary-40' as ColorVariant,
        startGradient: (startColor || 'primary-40') as ColorVariant,
        endGradient: (endColor || 'primary-80') as ColorVariant,
        direction: (direction || 'to-right') as GradientDirection,
      }
    }

    if (part.startsWith('solid:')) {
      const color = part.substring(6) as ColorVariant
      return {
        type: 'solid' as ColorType,
        solid: color,
        startGradient: 'primary-40' as ColorVariant,
        endGradient: 'primary-80' as ColorVariant,
        direction: 'to-right' as GradientDirection,
      }
    }

    if (part.startsWith('gradient:')) {
      const gradientPart = part.substring(9)
      const [colors, direction] = gradientPart.split(':')
      const [startColor, endColor] = colors.split('|')
      return {
        type: 'gradient' as ColorType,
        solid: 'primary-40' as ColorVariant,
        startGradient: (startColor || 'primary-40') as ColorVariant,
        endGradient: (endColor || 'primary-80') as ColorVariant,
        direction: (direction || 'to-right') as GradientDirection,
      }
    }

    // Handle gradient format without prefix: "primary-10|primary-5:to-right"
    // This happens when dark part is specified without "gradient:" prefix
    if (part.includes('|') && part.includes(':')) {
      const [colors, direction] = part.split(':')
      const [startColor, endColor] = colors.split('|')
      if (startColor && endColor) {
        return {
          type: 'gradient' as ColorType,
          solid: 'primary-40' as ColorVariant,
          startGradient: startColor as ColorVariant,
          endGradient: endColor as ColorVariant,
          direction: (direction || 'to-right') as GradientDirection,
        }
      }
    }

    if (part === 'transparent' || part.startsWith('transparent')) {
      return {
        type: 'transparent' as ColorType,
        solid: 'primary-40' as ColorVariant,
        startGradient: 'primary-40' as ColorVariant,
        endGradient: 'primary-80' as ColorVariant,
        direction: 'to-right' as GradientDirection,
      }
    }

    // Default to solid if format is unclear
    return {
      type: 'solid' as ColorType,
      solid: part as ColorVariant,
      startGradient: 'primary-40' as ColorVariant,
      endGradient: 'primary-80' as ColorVariant,
      direction: 'to-right' as GradientDirection,
    }
  }

  const light = parseColorPart(lightPart)
  const dark = parseColorPart(darkPart)

  return {
    colorType: {
      light: light.type,
      dark: dark.type,
    },
    colorSolid: {
      light: light.solid,
      dark: dark.solid,
    },
    colorStartGradient: {
      light: light.startGradient,
      dark: dark.startGradient,
    },
    colorEndGradient: {
      light: light.endGradient,
      dark: dark.endGradient,
    },
    colorGradientDirection: {
      light: light.direction,
      dark: dark.direction,
    },
  }
}

/**
 * Parse alignment format string
 * Format: "<type>:<value>" or multiple: "h:center v:start"
 * Can be responsive: "t:center ta:t:left de:t:right"
 * Example: "t:center" or "h:center v:start" or "t:center ta:t:left"
 */
export function parseAlignmentFormat(format: string): {
  textAlign?: ResponsiveValue<TextAlignment>
  hAlign?: ResponsiveValue<HorizontalAlignment>
  vAlign?: ResponsiveValue<VerticalAlignment>
  cAlign?: ResponsiveValue<ComponentAlignment>
} {
  const parts = format.trim().split(/\s+/)
  const textAlignParts: string[] = []
  const hAlignParts: string[] = []
  const vAlignParts: string[] = []
  const cAlignParts: string[] = []

  for (const part of parts) {
    // Check if it's a breakpoint-prefixed alignment
    const colonIndex = part.indexOf(':')
    if (colonIndex === -1) continue

    const prefix = part.substring(0, colonIndex)
    const rest = part.substring(colonIndex + 1)

    // Check if it's a breakpoint prefix
    if (BREAKPOINT_PREFIXES[prefix]) {
      // This is a breakpoint-prefixed value, check the next colon
      const secondColon = rest.indexOf(':')
      if (secondColon !== -1) {
        const alignType = rest.substring(0, secondColon)
        if (alignType === 't') {
          textAlignParts.push(part)
        } else if (alignType === 'h') {
          hAlignParts.push(part)
        } else if (alignType === 'v') {
          vAlignParts.push(part)
        } else if (alignType === 'c') {
          cAlignParts.push(part)
        }
      }
    } else {
      // This is a direct alignment type
      if (prefix === 't') {
        textAlignParts.push(part)
      } else if (prefix === 'h') {
        hAlignParts.push(part)
      } else if (prefix === 'v') {
        vAlignParts.push(part)
      } else if (prefix === 'c') {
        cAlignParts.push(part)
      }
    }
  }

  const result: {
    textAlign?: ResponsiveValue<TextAlignment>
    hAlign?: ResponsiveValue<HorizontalAlignment>
    vAlign?: ResponsiveValue<VerticalAlignment>
    cAlign?: ResponsiveValue<ComponentAlignment>
  } = {}

  if (textAlignParts.length > 0) {
    result.textAlign = parseResponsiveFormat(textAlignParts.join(' '), (value) => {
      // Extract the alignment value after "t:" or breakpoint prefix
      const match = value.match(/(?:^|:)(?:t:)?([a-z]+)$/)
      return (match ? match[1] : 'left') as TextAlignment
    })
  }

  if (hAlignParts.length > 0) {
    result.hAlign = parseResponsiveFormat(hAlignParts.join(' '), (value) => {
      // Extract the alignment value after "h:" or breakpoint prefix
      const match = value.match(/(?:^|:)(?:h:)?([a-z]+)$/)
      const alignValue = match ? match[1] : 'start'
      // Handle 'parent' as a valid HorizontalAlignment value
      if (alignValue === 'parent') return 'parent' as HorizontalAlignment
      return alignValue as HorizontalAlignment
    })
  }

  if (vAlignParts.length > 0) {
    result.vAlign = parseResponsiveFormat(vAlignParts.join(' '), (value) => {
      // Extract the alignment value after "v:" or breakpoint prefix
      const match = value.match(/(?:^|:)(?:v:)?([a-z]+)$/)
      const alignValue = match ? match[1] : 'start'
      return alignValue as VerticalAlignment
    })
  }

  if (cAlignParts.length > 0) {
    result.cAlign = parseResponsiveFormat(cAlignParts.join(' '), (value) => {
      // Extract the alignment value after "c:" or breakpoint prefix
      const match = value.match(/(?:^|:)(?:c:)?([a-z]+)$/)
      const alignValue = match ? match[1] : 'start'
      return alignValue as ComponentAlignment
    })
  }

  return result
}

/**
 * Parse limit input to LimitSettings structure
 *
 * Supports three formats:
 * 1. Simple number: `10` -> { enabled: true, limit: 100, value: 10 }
 * 2. Compact string: `"limit:10 en:false max:100"` -> { enabled: false, limit: 100, value: 10 }
 * 3. Object: `{ enabled: false, limit: 100, value: 10 }` -> { enabled: false, limit: 100, value: 10 }
 *
 * @param input - Limit input in any supported format
 * @param defaults - Optional defaults (defaults to { enabled: false, limit: 100, value: 10 })
 * @returns Parsed LimitSettings object
 */
export function parseLimitFormat(
  input: LimitInput | undefined,
  defaults: Partial<LimitSettings> = {}
): LimitSettings | undefined {
  if (input === undefined) {
    return undefined
  }

  const defaultSettings: LimitSettings = {
    enabled: defaults.enabled ?? false,
    limit: defaults.limit ?? 100,
    value: defaults.value ?? 10,
  }

  if (typeof input === 'number') {
    // Simple number format
    return {
      enabled: true,
      limit: defaultSettings.limit,
      value: input,
    }
  }

  if (typeof input === 'string') {
    // Compact string format: "limit:10" or "limit:10 en:false" or "limit:10 en:false max:100"
    const parts = input.split(/\s+/)
    let value = defaultSettings.value
    let enabled = defaultSettings.enabled
    let maxLimit = defaultSettings.limit

    for (const part of parts) {
      if (part.startsWith('limit:')) {
        value = parseInt(part.substring(6)) || defaultSettings.value
      } else if (part.startsWith('en:')) {
        enabled = part.substring(3) === 'true'
      } else if (part.startsWith('max:')) {
        maxLimit = parseInt(part.substring(4)) || defaultSettings.limit
      }
    }

    return {
      enabled,
      limit: maxLimit,
      value,
    }
  }

  // Object format
  return {
    enabled: input.enabled ?? defaultSettings.enabled,
    limit: input.limit ?? defaultSettings.limit,
    value: input.value ?? defaultSettings.value,
  }
}

/**
 * Parse spacing format string
 * Format: "<direction>:<value><unit>" or multiple: "pt:20px pb:20px"
 * Can be responsive: "p:1rem ta:p:2rem de:p:3rem" or "pt:12px pb:12px ta:pt:14px ta:pb:14px"
 * Supports padding (p, pt, pr, pb, pl), margin (m, mt, mr, mb, ml), and gap (gap)
 * Example: "p:1rem" or "pt:20px pb:20px" or "gap:12px" or "p:1rem ta:p:2rem" or "pt:12px ta:pt:14px"
 */
export function parseSpacingFormat(format: string): ResponsiveValue<SpacingInputValue> {
  // First, group breakpoint-prefixed parts by breakpoint
  const parts = format.trim().split(/\s+/)
  const breakpointGroups: Partial<Record<AppBreakpoint, string[]>> = {}
  const defaultParts: string[] = []

  // Spacing directions that should take priority over breakpoint prefixes
  const spacingDirections = ['pt', 'pr', 'pb', 'pl', 'mt', 'mr', 'mb', 'ml', 'p', 'm', 'gap']

  for (const part of parts) {
    const colonIndex = part.indexOf(':')
    if (colonIndex === -1) {
      defaultParts.push(part)
    } else {
      const prefix = part.substring(0, colonIndex)
      const value = part.substring(colonIndex + 1)

      // Check if this is a spacing direction FIRST (takes priority over breakpoint prefixes)
      // This ensures "pl:60px" is parsed as padding-left, not phone-landscape breakpoint
      // Only when we have nested format like "pl:p:40px" should we treat "pl" as breakpoint prefix
      const isSpacingDirection = spacingDirections.includes(prefix)

      if (isSpacingDirection) {
        // Check if the value contains another colon (e.g., "pl:p:40px" = phone-landscape: p:40px)
        // If so, it's a breakpoint-prefixed spacing value
        const secondColonIndex = value.indexOf(':')
        if (secondColonIndex !== -1) {
          // Has nested format (e.g., "pl:p:40px", "ta:pt:14px")
          // This is a breakpoint-prefixed spacing value
          const breakpoint = BREAKPOINT_PREFIXES[prefix]
          if (breakpoint) {
            if (!breakpointGroups[breakpoint]) {
              breakpointGroups[breakpoint] = []
            }
            breakpointGroups[breakpoint]!.push(value)
          } else {
            // Not a breakpoint prefix, treat as default spacing direction
            defaultParts.push(part)
          }
        } else {
          // Simple spacing direction (e.g., "pl:60px" = padding-left: 60px)
          defaultParts.push(part)
        }
      } else {
        // Not a spacing direction, check if it's a breakpoint prefix
        const breakpoint = BREAKPOINT_PREFIXES[prefix]
        if (breakpoint) {
          // This is a breakpoint-prefixed part (e.g., "ta:body-large")
          if (!breakpointGroups[breakpoint]) {
            breakpointGroups[breakpoint] = []
          }
          breakpointGroups[breakpoint]!.push(value)
        } else {
          // Not a breakpoint prefix and not a spacing direction, treat as default
          defaultParts.push(part)
        }
      }
    }
  }

  // Parse default value
  const defaultString = defaultParts.length > 0 ? defaultParts.join(' ') : ''
  const parseSpacingValue = (value: string): SpacingInputValue => {
    const spacing: SpacingInputValue = {
      top: { value: 0, unit: 'px' as SpacingUnit },
      right: { value: 0, unit: 'px' as SpacingUnit },
      bottom: { value: 0, unit: 'px' as SpacingUnit },
      left: { value: 0, unit: 'px' as SpacingUnit },
    }

    if (!value) return spacing

    const parts = value.split(/\s+/)
    for (const part of parts) {
      // Match spacing format: direction:value+unit
      // Note: Order matters - longer patterns (pt, pr, pb, pl) must come before shorter ones (p)
      // Support negative values: (-?\d+(?:\.\d+)?)
      const match = part.match(/(?:^|:)(pt|pr|pb|pl|mt|mr|mb|ml|p|m|gap):(-?\d+(?:\.\d+)?)(px|rem|em|%)$/)
      if (!match) continue

      const [, direction, val, unit] = match as [string, string, string, SpacingUnit]
      const numValue = parseFloat(val)

      switch (direction) {
        case 'p':
          spacing.top = { value: numValue, unit }
          spacing.right = { value: numValue, unit }
          spacing.bottom = { value: numValue, unit }
          spacing.left = { value: numValue, unit }
          break
        case 'pt':
          spacing.top = { value: numValue, unit }
          break
        case 'pr':
          spacing.right = { value: numValue, unit }
          break
        case 'pb':
          spacing.bottom = { value: numValue, unit }
          break
        case 'pl':
          spacing.left = { value: numValue, unit }
          break
        case 'm':
          spacing.top = { value: numValue, unit }
          spacing.right = { value: numValue, unit }
          spacing.bottom = { value: numValue, unit }
          spacing.left = { value: numValue, unit }
          break
        case 'mt':
          spacing.top = { value: numValue, unit }
          break
        case 'mr':
          spacing.right = { value: numValue, unit }
          break
        case 'mb':
          spacing.bottom = { value: numValue, unit }
          break
        case 'ml':
          spacing.left = { value: numValue, unit }
          break
        case 'gap':
          // Gap applies to all sides (top, right, bottom, left)
          spacing.top = { value: numValue, unit }
          spacing.right = { value: numValue, unit }
          spacing.bottom = { value: numValue, unit }
          spacing.left = { value: numValue, unit }
          break
        default:
          break
      }
    }

    return spacing
  }

  // Parse default value (applies to all breakpoints)
  const defaultSpacing = parseSpacingValue(defaultString)

  // Parse breakpoint-specific values
  const result: Partial<Record<AppBreakpoint, SpacingInputValue>> = {}
  for (const [breakpoint, parts] of Object.entries(breakpointGroups)) {
    // Parse breakpoint-specific spacing (don't merge with defaults - use parsed values directly)
    const breakpointSpacing = parseSpacingValue(parts.join(' '))
    // If any direction was explicitly set (even if 0), use the parsed value
    // Otherwise, inherit from default
    const hasExplicitTop = parts.some((p) => p.startsWith('pt:') || p.startsWith('p:'))
    const hasExplicitRight = parts.some((p) => p.startsWith('pr:') || p.startsWith('p:'))
    const hasExplicitBottom = parts.some((p) => p.startsWith('pb:') || p.startsWith('p:'))
    const hasExplicitLeft = parts.some((p) => p.startsWith('pl:') || p.startsWith('p:'))

    result[breakpoint as AppBreakpoint] = {
      top: hasExplicitTop ? breakpointSpacing.top : defaultSpacing.top,
      right: hasExplicitRight ? breakpointSpacing.right : defaultSpacing.right,
      bottom: hasExplicitBottom ? breakpointSpacing.bottom : defaultSpacing.bottom,
      left: hasExplicitLeft ? breakpointSpacing.left : defaultSpacing.left,
    }
  }

  // Apply inheritance rules
  const phone = result.phone ?? defaultSpacing
  const tablet = result.tablet ?? defaultSpacing
  const desktop = result.desktop ?? defaultSpacing

  return {
    phone: phone,
    'phone-landscape': result['phone-landscape'] ?? phone,
    tablet: tablet,
    'tablet-landscape': result['tablet-landscape'] ?? tablet,
    desktop: desktop,
    tv: result.tv ?? desktop,
  }
}

/**
 * Parse corner radius format string
 * Format: "<value>" or responsive: "small ta:medium de:large"
 * Example: "medium" or "small ta:medium de:large"
 * Valid values: none, extra-small, small, medium, large, extra-large, full
 */
export function parseCornerRadiusFormat(format: string): ResponsiveValue<CornerRadiusValue> {
  const validValues: CornerRadiusValue[] = ['none', 'extra-small', 'small', 'medium', 'large', 'extra-large', 'full']

  return parseResponsiveFormat(format, (value) => {
    // Validate and return the value, or fallback to 'medium' if invalid
    const normalized = value.trim().toLowerCase()
    if (validValues.includes(normalized as CornerRadiusValue)) {
      return normalized as CornerRadiusValue
    }
    // Fallback to 'medium' for invalid values
    return 'medium'
  })
}

/**
 * Parse aspect ratio format string
 * Format: "<value>" where value can be "auto" or ratio with dashes like "1-1", "16-9"
 * Converts dash format to colon format: "1-1" -> "1:1", "16-9" -> "16:9"
 * Example: "auto" or "1-1" or "16-9" or "1-1 ta:16-9"
 */
export function parseAspectRatioFormat(format: string): ResponsiveValue<AiAspectRatio> {
  return parseResponsiveFormat(format, (value) => {
    // Handle "auto" case
    if (value === 'auto') {
      return 'auto' as AiAspectRatio
    }
    // Convert dash format to colon format (e.g., "1-1" -> "1:1", "16-9" -> "16:9")
    const converted = value.replace(/-/g, ':')
    // Validate it's a valid aspect ratio format
    const validRatios = ['auto', '1:1', '4:3', '16:9', '3:2', '5:4', '7:5', '8:10', '9:16', '2:1', '21:9']
    if (validRatios.includes(converted)) {
      return converted as AiAspectRatio
    }
    // Fallback to auto if invalid
    return 'auto' as AiAspectRatio
  })
}

/**
 * Parse width format string
 * Format: "w:<value><unit>"
 * Example: "w:100%" or "w:300px"
 */
export function parseWidthFormat(format: string): ResponsiveValue<string[] | number[]> {
  return parseResponsiveFormat(format, (value) => {
    const match = value.match(/^w:(\d+(?:\.\d+)?)(px|%)$/)
    if (!match) return ['100%']

    const [, val, unit] = match
    if (unit === '%') {
      return [`${val}%`]
    } else {
      return [`${val}px`]
    }
  })
}

/**
 * Parse gap format string (simple spacing value)
 * Format: "gap:<value><unit>"
 * Example: "gap:10px" or "gap:1rem"
 */
export function parseGapFormat(format: string): ResponsiveValue<{ value: number; unit: SpacingUnit }> {
  return parseResponsiveFormat(format, (value) => {
    const match = value.match(/^gap:(\d+(?:\.\d+)?)(px|rem|em|%)$/)
    if (!match) {
      return { value: 0, unit: 'px' as SpacingUnit }
    }

    const [, val, unit] = match
    return {
      value: parseFloat(val),
      unit: unit as SpacingUnit,
    }
  })
}

/**
 * Parse table data source input
 * Supports string (table ID), simplified object, or full complex object
 *
 * @param dataSource - The data source input (string, simplified object, or full object)
 * @param options - Configuration options
 * @param options.tableKey - Key name for table ID ('table' or 'tableId')
 * @param options.includeIcon - Whether to include icon field in columns (default: false)
 * @param options.isDetailPageTable - Whether to include isDetailPageTable field (default: false)
 * @returns Parsed data source structure with table ID and columns
 */
export function parseTableDataSource<T extends BaseTableDataSource = BaseTableDataSource>(
  dataSource: string | any,
  options: {
    tableKey: 'table' | 'tableId'
    includeIcon?: boolean
    isDetailPageTable?: boolean
  }
): T {
  const { tableKey, includeIcon = false, isDetailPageTable = false } = options

  // Handle string input (just table ID)
  if (typeof dataSource === 'string') {
    const base = {
      table: dataSource,
      columns: includeIcon ? ([] as TableDataSourceColumnWithIcon[]) : ([] as TableDataSourceColumn[]),
      ...(isDetailPageTable && { isDetailPageTable: false }),
    }
    return base as unknown as T
  }

  // Handle simplified object structure (with tableId or table key)
  if (dataSource[tableKey] || dataSource.tableId || dataSource.table) {
    const tableId = dataSource[tableKey] || dataSource.tableId || dataSource.table
    const columns = (dataSource.columns || []).map((col: any) => {
      // Handle string (simple column ID or compact format)
      if (typeof col === 'string') {
        // Check if it's compact format (contains "col:")
        if (col.includes('col:')) {
          // Parse compact format: "col:Cf35zy lbl:ID ic:star us:false vs:true"
          const regex = /(col|lbl|ic|us|vs):([^\s]+(?:\s+[^\s:]+)*?)(?=\s+(?:col|lbl|ic|us|vs):|$)/g
          let columnId = ''
          let label: string | undefined
          let icon: string | undefined
          let userSpecific = false
          let visibility: ResponsiveValue<boolean> | undefined

          let match
          while ((match = regex.exec(col)) !== null) {
            const key = match[1]
            const value = match[2].trim()

            if (key === 'col') {
              columnId = value
            } else if (key === 'lbl') {
              label = value
            } else if (key === 'ic' && includeIcon) {
              icon = value
            } else if (key === 'us') {
              userSpecific = value === 'true'
            } else if (key === 'vs') {
              // Handle visibility
              if (value === 'true' || value === 'false') {
                const boolValue = value === 'true'
                visibility = {
                  phone: boolValue,
                  'phone-landscape': boolValue,
                  tablet: boolValue,
                  'tablet-landscape': boolValue,
                  desktop: boolValue,
                  tv: boolValue,
                }
              } else {
                // Parse responsive format: "vs:ph:true ta:false de:true"
                const vsParts = value.split(/\s+/)
                const vsMap: Partial<Record<string, boolean>> = {}
                for (const vsPart of vsParts) {
                  const colonIndex = vsPart.indexOf(':')
                  if (colonIndex !== -1) {
                    const breakpoint = vsPart.substring(0, colonIndex)
                    const val = vsPart.substring(colonIndex + 1)
                    vsMap[breakpoint] = val === 'true'
                  }
                }
                // Map short breakpoint names to full names
                // Breakpoint abbreviations match docs/app-builder/format/styles/breakpoints.md
                const breakpointMap: Record<string, string[]> = {
                  ph: ['phone'],
                  pl: ['phone-landscape'],
                  ta: ['tablet'],
                  tl: ['tablet-landscape'],
                  de: ['desktop'],
                  tv: ['tv'],
                }
                const vis: ResponsiveValue<boolean> = {
                  phone: true,
                  'phone-landscape': true,
                  tablet: true,
                  'tablet-landscape': true,
                  desktop: true,
                  tv: true,
                }
                for (const [short, full] of Object.entries(breakpointMap)) {
                  if (vsMap[short] !== undefined) {
                    for (const bp of full) {
                      vis[bp as keyof ResponsiveValue<boolean>] = vsMap[short]!
                    }
                  }
                }
                visibility = vis
              }
            }
          }

          if (!columnId) {
            throw new Error(`Invalid column format: missing col: prefix in "${col}"`)
          }

          const result: any = {
            col: columnId,
            id: columnId,
            label: label || columnId,
            userSpecific,
          }

          if (includeIcon) {
            result.icon = icon || 'star'
            // For data-table, always include visibility (defaults to all true if not specified)
            result.visibility = visibility || {
              phone: true,
              'phone-landscape': true,
              tablet: true,
              'tablet-landscape': true,
              desktop: true,
              tv: true,
            }
          } else if (visibility) {
            // For single-row, only include visibility if explicitly specified
            result.visibility = visibility
          }

          return result
        } else {
          // Simple string format (just column ID)
          const result: any = {
            col: col,
            id: col,
            label: col,
            userSpecific: false,
          }

          if (includeIcon) {
            result.icon = 'star'
            result.visibility = {
              phone: true,
              'phone-landscape': true,
              tablet: true,
              'tablet-landscape': true,
              desktop: true,
              tv: true,
            }
          }

          return result
        }
      }

      // Handle object (full column definition)
      let visibility: ResponsiveValue<boolean> | undefined
      if (col.visible === undefined || col.visible === true) {
        // Default: visible on all breakpoints
        visibility = {
          phone: true,
          'phone-landscape': true,
          tablet: true,
          'tablet-landscape': true,
          desktop: true,
          tv: true,
        }
      } else if (typeof col.visible === 'boolean') {
        // Single boolean value applies to all breakpoints
        visibility = {
          phone: col.visible,
          'phone-landscape': col.visible,
          tablet: col.visible,
          'tablet-landscape': col.visible,
          desktop: col.visible,
          tv: col.visible,
        }
      } else {
        // Object with breakpoint-specific values
        visibility = {
          phone: col.visible.phone ?? true,
          'phone-landscape': col.visible.tablet ?? true,
          tablet: col.visible.tablet ?? true,
          'tablet-landscape': col.visible.tablet ?? true,
          desktop: col.visible.desktop ?? true,
          tv: col.visible.desktop ?? true,
        }
      }

      const result: any = {
        col: col.columnId,
        id: col.columnId,
        label: col.label || col.columnId,
        userSpecific: false,
      }

      if (includeIcon) {
        result.icon = col.icon || 'star'
        // For data-table, always include visibility
        result.visibility = visibility
      } else if (visibility && Object.values(visibility).some((v) => v === false)) {
        // For single-row, only include visibility if it's not all true
        result.visibility = visibility
      }

      return result
    })

    const base = {
      table: tableId,
      columns,
      ...(isDetailPageTable
        ? { isDetailPageTable: dataSource.isDetailPageTable ?? false }
        : dataSource.isDetailPageTable !== undefined
          ? { isDetailPageTable: dataSource.isDetailPageTable }
          : {}),
    }

    return base as unknown as T
  }

  // If it's already the full complex object, use it directly
  return dataSource as unknown as T
}
